/**
 * The two lines on the checklist that frighten people, explained.
 *
 * `safety.ts` can tell a racer that a 9.60 car owes an NHRA competition licence
 * and a certified chassis. What it cannot do — because a checklist row is one
 * line long — is say what either of those *is*, and the two of them are where
 * this sport loses people. "Competition licence" sounds like an exam. "SFI
 * 25.5" sounds like a part number you are supposed to already know. Both read,
 * to somebody whose car is getting quicker, like a door with a stranger behind
 * it.
 *
 * Neither is that. The licence is a Saturday at your own track in your own car
 * with two people watching. The certification is somebody with a set of
 * callipers measuring your tubing. This module says so, in the order a racer
 * meets it, with the rulebook's numbers attached so that none of it has to be
 * taken on trust.
 *
 * Three rules it keeps.
 *
 * **Say what the pace needs, not what the sport contains.** A 12-second car is
 * told it needs no licence and has nothing to certify — and told which numbers
 * would change that — rather than handed a table of seven levels to find itself
 * in. The whole ladder lives in `licenceFor` and `chassisFor`; exactly one rung
 * of it is ever returned.
 *
 * **Quote, and cite.** Every threshold here is the rulebook's own, in the
 * racer's own units, sourced to the paragraph it comes from: the licence breaks
 * from the NHRA Competition License Regulations printed on the application
 * (revised July 2026), the chassis breaks from General Regulations 4:11 in the
 * 2026 NHRA Rulebook. Reassurance that cannot be checked is just cheerfulness.
 *
 * **Name what this page will not guess.** There is one narrow band — a car
 * slower than 10.00 that trips 135 mph — where the cage is required by one
 * sentence and the certification sentence is written on an ET the car does not
 * run. That case gets `standing: 'ask'` and a phone number, not an answer. See
 * `chassisFor`.
 */

import { CHASSIS_INSPECTOR, LICENCE_APPLICATION_67, LICENCE_INFORMATION, SFI_CHASSIS } from './sources';
import type { ChassisLevel, Fuel, VehicleType } from './spec';
import { OPEN_BODIED, hasCage, hasRollBar, isCertified } from './spec';

export interface CredentialContext {
  /** Quarter-mile ET, seconds — the axis both ladders are written on. */
  et: number;
  /**
   * Trap speed, mph, or null when unknown.
   *
   * It carries more weight here than anywhere else on the page: the licence
   * rule is "9.99 or quicker **or** 135 mph or faster", so a 10.30 car that
   * traps 137 needs a licence its ET alone would never have asked for. An
   * unknown speed fires nothing, exactly as in `safety.ts`, and the UI says so.
   */
  mph: number | null;
  vehicleType: VehicleType;
  /** What the car has today — which decides whether this is a build or a recert. */
  chassis: ChassisLevel;
  /** Methanol puts a dragster on the Top Alcohol chassis spec rather than the ET one. */
  fuel: Fuel;
}

export interface Step {
  /** The thing to do, in four or five words. */
  what: string;
  /** What it actually involves — the part that takes the fear out. */
  detail: string;
}

export interface Fact {
  label: string;
  value: string;
}

/**
 * Where the racer stands.
 *
 * `ask` is not a hedge for anything this page finds hard; it is reserved for
 * the one combination the published rules genuinely leave to an official (see
 * the module note). Using it anywhere else would make the other two worthless.
 */
export type Standing = 'required' | 'not-required' | 'ask';

export interface Credential {
  key: string;
  /** What the thing is called on the checklist, so the two read as one answer. */
  title: string;
  /** The verdict for this pace: a level, a spec number, or "nothing to do". */
  headline: string;
  standing: Standing;
  /** The rule that put them here, in their own units. */
  why: string;
  /** The paragraph that does the actual work of making it un-scary. */
  lede: string;
  steps: Step[];
  facts: Fact[];
  /** What it is *not* — the sentence that answers the fear rather than the question. */
  calm: string;
  links: { label: string; url: string }[];
}

/** An unknown trap speed fires nothing — never zero, never fast. Same rule as safety.ts. */
const atLeast = (mph: number | null, limit: number) => mph !== null && mph >= limit;

// ---------------------------------------------------------------------------
// The licence
// ---------------------------------------------------------------------------

/**
 * The licensing runs, which are the same six for every level that requires them.
 *
 * Written out rather than summarised as "six passes" because the shape of them
 * is the reassurance: you do not walk up to a full pass. You make a half pass,
 * then three moderate ones, and only then the two that count — which is how a
 * careful driver would introduce themselves to a new car whether or not anybody
 * made them.
 *
 * `standard` is the only thing that moves between levels — the number the two
 * full passes have to beat — so it is a parameter rather than four copies of
 * the same five steps with one sentence edited.
 */
function runSteps(standard: string): Step[] {
  return [
    {
      what: 'Tell the track before you do anything',
      detail:
        'You arrange the session, not NHRA. Tell the track manager or an authorised track official what you are there for, then line up two currently licensed NHRA drivers of equal or higher class and one track official to watch every run and sign for it. Most tracks will help you find the two drivers — the licence number and expiry go on the form beside each signature.',
    },
    {
      what: 'The cockpit orientation test',
      detail:
        'The "blindfold test". A licensed driver or track official administers it and signs it off; the rulebook says no more than that. In practice it is somebody making sure that, without looking, you can find and work the belts, the shutoff and everything else you would be reaching for in a hurry.',
    },
    {
      what: 'Six passes, in this order',
      detail:
        'One half pass, three moderate passes, then two full passes. All single runs — no side-by-side — at an NHRA member track, with the signatures collected after each one. Level 4–7 runs may all be made at an eighth-mile track.',
    },
    {
      what: 'Two full passes at the number',
      detail: `${standard} The approval team can ask for more runs; that is a normal outcome, not a failure.`,
    },
    {
      what: 'Mail it in',
      detail:
        'The completed application, the original time slips and payment. Originals only — photocopies, emails and faxes are not accepted for anything with test runs on it, though renewals can go back by email. Allow one to two weeks.',
    },
  ];
}

/** The one licensing rule most likely to catch somebody out, worth repeating on every tier. */
const RUN_CLOCK: Fact = {
  label: 'How long runs stay good',
  value: 'One year. Older than that and runs 4, 5 and 6 have to be done again.',
};

const LICENCE_LINKS = [
  { label: 'Level 6 & 7 sportsman application (PDF)', url: LICENCE_APPLICATION_67 },
  { label: 'All NHRA licence forms & regulations', url: LICENCE_INFORMATION },
];

/**
 * Which licence a pace needs.
 *
 * The breaks are the ones printed on the application: Level 6 is "7.50 – 9.99
 * or >135 mph", Level 7 is "10.00 & slower, under 135 mph", and the general
 * regulation that makes any of it compulsory reads "a dial-in of 9.99 or
 * quicker (4.50–6.39 in the 1/8 Mile), or 135 mph or faster".
 *
 * Note the shape of the first test. It is the *speed* half that decides whether
 * a licence is needed at all for a car slower than 10.00 — which is why a
 * 10.30 car trapping 137 lands on Level 6 rather than on "not required", and
 * why this function tests "needs one at all" before it tests the ET bands.
 */
export function licenceFor(ctx: CredentialContext): Credential {
  const needed = ctx.et <= 9.99 || atLeast(ctx.mph, 135);

  if (!needed) return unlicensed();
  if (ctx.et > 7.49) return level6(ctx);
  if (ctx.et >= 6.0) return level3();
  return level2();
}

/** 10.00 and slower, under 135 mph: the licence people assume they need and do not. */
function unlicensed(): Credential {
  return {
    key: 'licence',
    title: 'NHRA competition licence',
    headline: 'Not required at this pace',
    standing: 'not-required',
    why: 'A competition licence is required at 9.99 or quicker (4.50–6.39 in the eighth), or at 135 mph and faster. You are on the other side of both.',
    lede:
      'This is the answer most people are surprised by. At this pace nobody is going to ask you for a competition licence: your state driver’s licence, the tech card you fill in at the gate, and a helmet are the list. You can race every weekend of the season and never touch an NHRA form.',
    steps: [
      {
        what: 'What the track will actually ask for',
        detail:
          'A tech card, a helmet to the current spec, and the licence in your wallet. Everything the checklist above marks required at this pace is equipment, not paperwork.',
      },
      {
        what: 'If you want an NHRA number anyway',
        detail:
          'A Level 7 competition number is the one licence you can have without making a single test run: a state driver’s licence beyond a learner’s permit stands in for them. Fill in sections 1, 2, 3 and 5 of the sportsman application and send it in. Without a driver’s licence beyond a learner’s permit, you do the cockpit orientation test and all six passes like anyone else.',
      },
    ],
    facts: [
      { label: 'What it costs', value: 'Level 7: $240 for two years, $140 for one — mandatory NHRA digital membership included.' },
      { label: 'Medical', value: 'None. No physical is required below Level 3.' },
      { label: 'Minimum age', value: 'The sportsman application is 18 and over; younger drivers go through NHRA licensing.' },
    ],
    calm:
      'Watch the speed half of the rule rather than the ET. A 10.30 car that trips 137 mph is over the line on trap speed alone, and the licence is a great deal easier to arrange in March than in the staging lanes.',
    links: LICENCE_LINKS,
  };
}

/** 7.50–9.99, or over 135 mph: the licence nearly every quick street car ends up needing. */
function level6(ctx: CredentialContext): Credential {
  const onSpeed = ctx.et > 9.99;
  return {
    key: 'licence',
    title: 'NHRA competition licence',
    headline: 'Level 6',
    standing: 'required',
    why: onSpeed
      ? `Level 6 is 7.50–9.99 or over 135 mph, and at ${Math.round(ctx.mph ?? 0)} mph you are there on speed alone — the ET half of the rule never had to fire.`
      : 'Level 6 covers 7.50 – 9.99 in the quarter (4.50 – 6.39 in the eighth), or anything over 135 mph.',
    lede:
      'A Level 6 licence is a day at your own track in your own car. There is no written test, no medical, and no NHRA official flown in to watch: you make six passes with two licensed drivers and a track official signing for each one, and you post the paperwork. The hardest part is arranging the day.',
    steps: [
      {
        what: 'Join NHRA and take the form with you',
        detail:
          'Membership is compulsory for licensed competitors and comes bundled with the licence fee, so it is one payment. The Level 6/7 sportsman application is a single PDF — print it before you go, because the signatures are collected trackside.',
      },
      ...runSteps('Both full passes must be 9.99 or quicker, or 135 mph or faster — the Level 4 and 6 standard.'),
    ],
    facts: [
      { label: 'What it costs', value: '$270 for two years, $170 for one — mandatory NHRA digital membership included.' },
      { label: 'Medical', value: 'None. The physical exam starts at Level 3.' },
      { label: 'How long it lasts', value: 'One or two years from the end of the month it is processed.' },
      RUN_CLOCK,
      {
        label: 'If you already hold Level 7',
        value: 'Staying in the same car, the track official can waive runs 1–3 — so it is the blindfold test and three passes.',
      },
      {
        label: 'If you race the index classes',
        value:
          'The form says it outright: Super Comp, Super Gas, Super Street, Stock and Super Stock “are not available with this application”. Those are the Level 4/5 pro-sportsman form instead — the same ET break and the same runs, a different sheet of paper.',
      },
    ],
    calm:
      'Nobody is grading your reaction time. The half pass and the three moderate ones exist so that two people who have done this watch you creep up on the car before you make a pass that counts — which is what you would want to do anyway, licence or no licence.',
    links: LICENCE_LINKS,
  };
}

/** 6.00–7.49: the first rung where a doctor is involved. */
function level3(): Credential {
  return {
    key: 'licence',
    title: 'NHRA competition licence',
    headline: 'Level 3',
    standing: 'required',
    why: 'Past 7.49 the sportsman levels run out. Advanced ET and the heads-up categories are Level 3 (Top Dragster and Top Sportsman at 7.99 or quicker), and which category you apply for depends on the class you enter, not on the ET alone.',
    lede:
      'The driving half is the same day it was at Level 6 — the blindfold test and the same six passes. Two things are added: a physical examination, and a rule about where the last two runs are made. Neither is a hurdle so much as a diary entry.',
    steps: [
      {
        what: 'The physical, first',
        detail:
          'Level 1–3 applicants pass an NHRA physical and hand the original completed form to the track official before any test runs are made — that order is the rule, not a suggestion. It needs a wet-ink signature from a physician with MD or DO after their name.',
      },
      ...runSteps(
        'Both full passes have to come in at or better than the standard for the category you are applying for — 7.49 or quicker for Advanced ET and the heads-up categories, 7.99 for Top Dragster and Top Sportsman.',
      ),
      {
        what: 'Runs 5 and 6 on a quarter-mile track',
        detail:
          'Runs 1–4 can be made in the eighth, but at Level 2 or 3 the two full passes have to be over a quarter for an unrestricted licence. Do them in the eighth and the licence is issued with an eighth-mile restriction on it — which is a real licence, just not everywhere.',
      },
    ],
    facts: [
      { label: 'Medical', value: 'Every two years. The licence expires on the physical date rather than the processing date.' },
      { label: 'Which form', value: 'The Level 2/3 pro-sportsman application, not the sportsman one.' },
      RUN_CLOCK,
      {
        label: 'Coming up from Level 7',
        value: 'An upgrade to Level 1, 2 or 3 is the full new-driver requirement — no runs are waived.',
      },
    ],
    calm:
      'The physical is an ordinary examination, and the list of things it rules out is short and specific — it is printed on the application, so there are no surprises in the envelope. If you have any doubt about a medication on that list, the licensing department would rather have the phone call than the form.',
    links: LICENCE_LINKS,
  };
}

/** 5.99 and quicker: honest about being the wrong page for the question. */
function level2(): Credential {
  return {
    key: 'licence',
    title: 'NHRA competition licence',
    headline: 'Level 2 or Level 1 — call NHRA',
    standing: 'required',
    why: 'At 5.99 and quicker you are in the alcohol and professional categories, where the standard is set per category rather than by a general ET band — Top Alcohol at 6.00 and 205 mph, Pro Mod at 6.70 and 190, Mountain Motor Pro Stock at 6.50 and 200.',
    lede:
      'This is the one place where the useful answer is a phone number. The category decides the level, the level decides the medical and the runs, and new Level 1 and Level 2 drivers can also be asked for a qualifying run or two at a national event before they are turned loose in competition.',
    steps: [
      {
        what: 'Ring the licensing department',
        detail: 'NHRA licensing, (888) 275-9375 or licensing@nhra.com. They will tell you the category and the form in one call, and they take this call constantly.',
      },
      {
        what: 'Expect an annual or two-yearly physical',
        detail: 'Level 1–3 is a physical every two years; Top Fuel and Funny Car are annual.',
      },
      { what: 'Then the same six runs', detail: 'The blindfold test and runs 1–6, with runs 5 and 6 over a quarter mile for an unrestricted licence.' },
    ],
    facts: [
      { label: 'Which form', value: 'Level 1 professional, or the Level 2/3 pro-sportsman application.' },
      RUN_CLOCK,
    ],
    calm:
      'Everybody at this end of the sport went through the same six passes you would be making. The paperwork gets longer; the day at the track does not change shape.',
    links: LICENCE_LINKS,
  };
}

// ---------------------------------------------------------------------------
// The chassis
// ---------------------------------------------------------------------------

/**
 * The SFI chassis specs, as SFI titles them.
 *
 * Quoted rather than paraphrased, because the whole difficulty of "which SFI
 * level do I need" is that the answer turns on three things at once — ET,
 * weight and how the car is built — and a summary that drops one of them is how
 * somebody builds the wrong cage. A racer reading "7.50 to 8.49 seconds, 3,600
 * lbs. maximum, tube chassis" against "…OEM frame or uni-body" can see which
 * one is their car. "You need 25.4" cannot be checked against anything.
 */
export interface ChassisSpec {
  spec: string;
  title: string;
}

const SFI_25_1K: ChassisSpec = { spec: 'SFI 25.1K', title: 'Tube chassis — 7.49 seconds and quicker, 2,800 lb maximum' };
const SFI_25_2E: ChassisSpec = { spec: 'SFI 25.2E', title: 'Tube chassis — 6.00 to 7.49 seconds, 3,200 lb maximum' };
const SFI_25_3D: ChassisSpec = {
  spec: 'SFI 25.3D',
  title: 'Aftermarket body shell and tube frame, OEM body and frame, or uni-body — 6.50 to 7.49 seconds, 3,600 lb maximum',
};
const SFI_25_4D: ChassisSpec = { spec: 'SFI 25.4D', title: 'Tube chassis — 7.50 to 8.49 seconds, 3,600 lb maximum' };
const SFI_25_5E: ChassisSpec = {
  spec: 'SFI 25.5E',
  title: 'OEM frame or uni-body, stock or modified floorpan and firewall — 7.50 to 8.49 seconds, 3,600 lb maximum',
};
const SFI_25_6A: ChassisSpec = {
  spec: 'SFI 25.6A',
  title: 'Stock car or truck, OEM frame or uni-body — 7.99 to 8.49 seconds, 5,000 lb maximum',
};

/**
 * The specs that could be this full-bodied car's, by ET band.
 *
 * Several per band on purpose: within one band the specs are separated by
 * construction and weight, which this page has no business deciding for
 * somebody — the 3,600 lb ceiling on 25.4D and 25.5E and the 5,000 lb one on
 * 25.6A are exactly the sort of thing a racer knows about their own car and a
 * form does not. Listed, with the ceilings on them, so the reader can pick.
 */
function fullBodiedSpecs(et: number): ChassisSpec[] {
  if (et >= 7.5) return [SFI_25_4D, SFI_25_5E, SFI_25_6A];
  if (et >= 6.5) return [SFI_25_3D, SFI_25_2E, SFI_25_1K];
  if (et >= 6.0) return [SFI_25_2E, SFI_25_1K];
  return [SFI_25_1K];
}

const CHASSIS_LINKS = [
  { label: 'NHRA chassis inspector locator', url: CHASSIS_INSPECTOR },
  { label: 'SFI drag racing chassis specs', url: SFI_CHASSIS },
];

/**
 * How a certification is obtained — the same four steps whatever the spec is.
 *
 * The first one is the expensive one, and it is the only one that is a decision
 * rather than an errand: the tag names a quickest permitted ET and a maximum
 * permitted weight, and you may not run quicker or heavier than what it says.
 * A cage built to the ET the car runs today is a cage to be built again.
 */
const CHASSIS_STEPS: Step[] = [
  {
    what: 'Build to the ET you are aiming at',
    detail:
      'The certification tag names a "Quickest Permitted e.t." and a "Maximum Permitted Weight", and the car may not be run past either. Pick the spec for where the combination is going, not where it is — the difference between an 8.50 cage and a 7.50 cage is a few tubes at build time and a whole second cage afterwards.',
  },
  {
    what: 'Find an inspector',
    detail:
      'NHRA publishes a chassis inspector locator. Certifications are also done at NHRA national events and Lucas Oil divisional events, or by prior arrangement through your division office — so a divisional weekend you were going to anyway is often the easiest appointment you will get.',
  },
  {
    what: 'Make the cage readable',
    detail:
      'The inspector has to see the structure, so anything trimmed, panelled or upholstered over it comes off first. They measure tube diameter and wall thickness, look at the welds — no ground welds, visible reinforcement on butt welds — check how the cage is tied to the frame or floor, and work through the geometry against the illustration for your body style.',
  },
  {
    what: 'The sticker goes on the frame',
    detail:
      'A serialised sticker, affixed before the car runs at any NHRA member track. That tag is what tech is looking for; an uncertified cage, however well built, is not a certified one.',
  },
  {
    what: 'Diary it — three years',
    detail:
      'Sportsman chassis are recertified every three years. It is the single most missed date in a race trailer, because nothing about the car changes on the day it expires.',
  },
];

/**
 * What this car has to certify, if anything.
 *
 * The ladder is General Regulations 4:11 in the 2026 rulebook, which splits
 * three ways and is worth reading in the order it is written:
 *
 *   "Open-bodied cars running 9.99 and quicker and/or faster than 135 mph, all
 *    rear-engine dragsters, and all street roadsters must meet applicable SFI
 *    Specification for e.t. Full-bodied cars running 8.49 and quicker and/or
 *    exceeding 180 mph must meet applicable SFI Specification for e.t. and
 *    weight. Full-bodied cars running between 8.50 and 9.99 and slower than
 *    180 mph must meet the NHRA Full-Bodied roll cage requirements found in the
 *    illustration in this section. Front-engine dragsters, altereds, and Funny
 *    Cars running slower than 10.00 seconds must meet their respective NHRA
 *    roll cage requirements."
 *
 * So the answer to "which SFI level do I need" is, for most of the cars this
 * page sees, *none* — an NHRA cage to the rulebook's own illustration, with a
 * serialised sticker on it. That is worth saying plainly: a racer who thinks
 * they need an SFI number at 9.20 is shopping for the wrong cage.
 *
 * The one gap is deliberate. A full-bodied car slower than 10.00 that trips
 * 135 mph is required to have a roll cage (4:11, and the E.T. quick-reference
 * chart's "Y / 135 mph"), but the sentence that requires a *sticker* is written
 * on "9.99 or quicker e.t. chassis". This page will not resolve that in either
 * direction — telling someone they need no tag is expensive if a tech inspector
 * disagrees, and telling them they do is expensive today. It returns `ask`.
 */
export function chassisFor(ctx: CredentialContext): Credential {
  const open = OPEN_BODIED.includes(ctx.vehicleType) && ctx.vehicleType !== 'Convertible';
  return open ? openBodiedChassis(ctx) : fullBodiedChassis(ctx);
}

/**
 * Convertibles are handled as full-bodied here, and as open-bodied in
 * `safety.ts`. Both are right for the question being asked: that file is
 * deciding between a window net and arm restraints, where a convertible has
 * nowhere to hang a net; this one is choosing a roll-cage illustration, and
 * NHRA draws its own between full-bodied cars and street roadsters, altereds
 * and dragsters. The convertible's separate rule — a roll bar at 13.49 rather
 * than 11.49 — lands in the checklist, where it belongs.
 */
function fullBodiedChassis(ctx: CredentialContext): Credential {
  const certified = isCertified(ctx.chassis);
  const base = {
    key: 'chassis',
    title: 'Chassis certification',
    links: CHASSIS_LINKS,
  };

  // Nothing at all: slower than 10.00 and under 135 mph.
  if (ctx.et > 9.99 && !atLeast(ctx.mph, 135)) {
    return {
      ...base,
      headline: 'Nothing to certify at this pace',
      standing: 'not-required',
      why: 'Certification is required at 9.99 and quicker. A roll bar arrives earlier — 11.49, or 13.49 in a convertible — but a roll bar is not certified and carries no tag.',
      lede:
        'The two get confused constantly, and the difference is worth having straight before you spend anything. A roll bar is a bar you or a shop can build to the dimensions printed in the rulebook, and nobody inspects it beyond the tech line. A certified chassis is a structure a licensed chassis inspector has measured and put a serialised sticker on. At this pace you may need the first; you do not need the second.',
      steps: [
        {
          what: 'If you are building anyway',
          detail:
            'Build for the ET you expect to run rather than the one you run now. The bar that satisfies 11.49 is not the cage that satisfies 9.99, and the cheap moment to find that out is before the tubing is cut.',
        },
        {
          what: 'When it starts to matter',
          detail:
            '9.99 in the quarter, 6.39 in the eighth, or 135 mph. Cars that go quicker generally do it in the same season they were "nearly there" the year before.',
        },
      ],
      facts: [
        { label: 'Roll bar', value: 'Required at 11.49 and quicker (13.49 in a convertible). Not certified, no tag.' },
        {
          label: 'Roll cage',
          value: 'Required at 9.99 and quicker, or over 135 mph. The certification rule is written on the 9.99 half of that.',
        },
      ],
      calm:
        'If a tech inspector has ever asked to see your bar, that is not the same as certification and it does not expire. There is no clock running on your car today.',
    };
  }

  // Required at 9.99, but the tag rule is written on an ET this car does not run.
  if (ctx.et > 9.99) {
    return {
      ...base,
      headline: 'A cage, yes — the tag is a question for tech',
      standing: 'ask',
      why: `Over 135 mph a roll cage is required whatever the ET, and you are at ${Math.round(ctx.mph ?? 0)} mph. The certification sentence, though, is written on "9.99 or quicker e.t. chassis" — and this car is slower than that.`,
      lede:
        'This is the one combination on this page that the published rules do not settle, so it is not going to be settled here either. The cage is not in doubt. Whether it has to carry a serialised sticker at an ET slower than 10.00 is the sort of thing your division office answers in a minute and a message board argues about for a year.',
      steps: [
        {
          what: 'Ask your division office',
          detail:
            'Certification arrangements go through them anyway, and the answer for your car and class comes with the appointment. Ask before the cage is built rather than after.',
        },
        {
          what: 'Assume the cage itself',
          detail:
            'Whatever the answer on the tag, the structure has to meet the NHRA full-bodied roll cage requirements — the illustration in general regulations 4:11 — so building to it is not wasted either way.',
        },
      ],
      facts: [
        { label: 'What is certain', value: 'A roll cage, over 135 mph, at any ET.' },
        { label: 'What is not', value: 'Whether a car slower than 10.00 needs the serialised sticker.' },
        { label: 'Who decides', value: 'The division office and the tech inspector — never this page.' },
      ],
      calm:
        'Being in a grey area of a rulebook is ordinary. The people who administer it are used to the question, and asking it early costs nothing.',
    };
  }

  // 8.50–9.99 under 180 mph: NHRA's own cage, NHRA's own tag, no SFI number.
  if (ctx.et >= 8.5 && !atLeast(ctx.mph, 180)) {
    return {
      ...base,
      headline: 'NHRA certified cage — no SFI spec number',
      standing: 'required',
      why: 'Full-bodied cars running between 8.50 and 9.99 and slower than 180 mph meet the NHRA full-bodied roll cage requirements — the illustration in general regulations 4:11 — and carry a serialised NHRA sticker.',
      lede:
        'Worth saying loudly, because it is the single most common thing people get wrong on the way to a nine-second car: at this pace you do not need an SFI-spec chassis. SFI numbers on full-bodied cars start at 8.49. What you need is a cage built to NHRA’s own drawing, inspected, and stickered.',
      steps: CHASSIS_STEPS,
      facts: [
        { label: 'Which spec', value: 'NHRA full-bodied roll cage requirements. No SFI 25.x number applies until 8.49 or 180 mph.' },
        { label: 'Recertification', value: 'Every three years.' },
        { label: 'The tag names', value: 'A quickest permitted ET and a maximum permitted weight. Exceed either and the certification does not cover you.' },
        { label: 'Where it is done', value: 'NHRA national events, Lucas Oil divisional events, or by arrangement through your division office.' },
        ...(certified
          ? [{ label: 'You already have a tag', value: 'Then this is a recertification date to keep, not a build to plan.' }]
          : hasCage(ctx.chassis)
            ? [{ label: 'You have a cage already', value: 'An uncertified cage still needs inspecting and tagging — and an inspector may want changes to what is there.' }]
            : hasRollBar(ctx.chassis)
              ? [{ label: 'You have a bar', value: 'A six-point bar does not become a cage by adding a sticker. This is new fabrication.' }]
              : []),
      ],
      calm:
        'The word "certification" does the damage. What happens is that somebody with a set of callipers spends half an hour on your tubing and your welds, and anything wrong gets written down as something to change — inspectors would far rather fix a car than fail one. The expensive mistake is not failing; it is building to the wrong ET because nobody asked what the car would run when it was finished.',
    };
  }

  // 8.49 and quicker, or over 180 mph: an SFI spec, chosen by ET, weight and build.
  //
  // The 180 mph half of that rule can land a car outside every band the specs
  // name — an 8.80 that traps 182 is over the speed line but slower than the
  // 8.49 the quickest full-bodied spec starts at. The bands are still the right
  // thing to show, with a line saying that the reader is between them.
  const specs = fullBodiedSpecs(ctx.et);
  const betweenBands = ctx.et > 8.49;
  return {
    ...base,
    headline: specs.map((s) => s.spec).join(' · '),
    standing: 'required',
    why: betweenBands
      ? 'Full-bodied cars exceeding 180 mph meet the applicable SFI specification for their ET and weight, whatever the ET is.'
      : 'Full-bodied cars running 8.49 and quicker meet the applicable SFI specification for their ET and weight.',
    lede:
      'Here the SFI numbers finally do apply — and which of them is yours turns on three things at once: the ET, the weight, and whether the car sits on a tube chassis or on its original frame or uni-body. That is why the rulebook names several and this page will not pick one for you. Read them against the car; the inspector will do the same.',
    steps: CHASSIS_STEPS,
    facts: [
      ...specs.map((s) => ({ label: s.spec, value: s.title })),
      ...(betweenBands
        ? [
            {
              label: 'Your ET is outside these bands',
              value:
                'You are over the 180 mph line at an ET slower than any full-bodied SFI spec covers, so which one applies is the inspector’s call. These are the nearest, and the division office will name the right one.',
            },
          ]
        : []),
      { label: 'Recertification', value: 'Every three years for these specs — the professional ones are annual.' },
      { label: 'The tag names', value: 'A quickest permitted ET and a maximum permitted weight. Exceed either and the certification does not cover you.' },
      ...(certified
        ? [{ label: 'You already have a tag', value: 'Check what ET and weight it was written for before you assume it covers this combination.' }]
        : []),
    ],
    calm:
      'A spec number is a drawing with dimensions on it, not a secret. Any shop that builds cages for a living works to these every week, and the one question worth asking them before anything is cut is "what will this car run, and what will it weigh?"',
  };
}

/**
 * Dragsters, roadsters and altereds.
 *
 * Different from the full-bodied path in one important way: two of these body
 * styles carry an SFI spec at *any* ET. "All rear-engine dragsters, and all
 * street roadsters must meet applicable SFI Specification for e.t." has no
 * speed or ET qualifier on it — a 12-second rail is still a rail.
 */
function openBodiedChassis(ctx: CredentialContext): Credential {
  const base = { key: 'chassis', title: 'Chassis certification', links: CHASSIS_LINKS, standing: 'required' as const };
  const alcohol = ctx.fuel === 'Methanol (alcohol)';

  if (ctx.vehicleType === 'Dragster') {
    const specs: ChassisSpec[] =
      ctx.et <= 5.99 && alcohol
        ? [{ spec: 'SFI 2.1C', title: 'Rear-engine dragster, Top Alcohol / methanol — 5.99 seconds and quicker' }]
        : ctx.et <= 7.49
          ? [{ spec: 'SFI 2.5E', title: 'Rear-engine dragster — 6.00 to 7.49 seconds' }]
          : [{ spec: 'SFI 2.7E', title: 'Rear-engine dragster — 7.50 seconds and slower' }];
    return {
      ...base,
      headline: specs.map((s) => s.spec).join(' · '),
      why: 'All rear-engine dragsters must meet the applicable SFI specification for their ET — there is no ET or speed below which a rail is exempt.',
      lede:
        'A dragster is the one body style where the answer does not depend on how quick you are: the chassis is the car, and it carries a spec and a tag from its first pass. The good news is that almost every dragster you can buy was built and certified to one of these already — the question for a used car is what the tag says and when it runs out.',
      steps: CHASSIS_STEPS,
      facts: [
        ...specs.map((s) => ({ label: s.spec, value: s.title })),
        {
          label: 'Front-engine cars',
          value:
            'A front-engine dragster slower than 10.00 meets NHRA’s own front-engine dragster roll cage requirements instead; quicker than that, it has SFI specs of its own (2.4 and 2.6).',
        },
        { label: 'Recertification', value: 'Every three years for the sportsman specs.' },
        {
          label: 'Spec and tag are one errand',
          value: 'A chassis “meets” an SFI spec by being inspected and certified to it — there is no version of this where you build to the spec and skip the sticker.',
        },
        { label: 'Buying used', value: 'Read the tag before the trailer leaves: it names the quickest ET and the heaviest weight the car may be run at.' },
      ],
      calm:
        'Nothing here is a judgement on your driving or your build. A certification is a date and a set of dimensions, and a dragster that has one is a dragster somebody has already done this work on.',
    };
  }

  if (ctx.vehicleType === 'Roadster') {
    const spec: ChassisSpec =
      ctx.et <= 7.49
        ? { spec: 'SFI 10.6', title: 'Side-steer roadster roll cage — 7.49 seconds and quicker' }
        : { spec: 'SFI 10.4B', title: 'Side-steer roadster roll cage — 7.50 seconds and slower' };
    return {
      ...base,
      headline: spec.spec,
      why: 'All street roadsters must meet the applicable SFI specification for their ET — like the dragsters, with no ET below which the rule stops applying.',
      lede:
        'Roadsters get their own specification rather than sharing the full-bodied one, because the structure around the driver is doing a different job. It is the same errand as any other certification: an inspector, a set of measurements, a serialised sticker.',
      steps: CHASSIS_STEPS,
      facts: [
        { label: spec.spec, value: spec.title },
        { label: 'Recertification', value: 'Every three years.' },
        {
          label: 'Spec and tag are one errand',
          value: 'A chassis “meets” an SFI spec by being inspected and certified to it, so the specification and the sticker arrive together.',
        },
        { label: 'The tag names', value: 'A quickest permitted ET and a maximum permitted weight.' },
      ],
      calm:
        'The spec exists because somebody worked out what a roadster needs, not because the sport wants your car off the property. An inspector who wants a change is telling you what to weld, not showing you the gate.',
    };
  }

  // Altered.
  if (ctx.et > 9.99 && !atLeast(ctx.mph, 135)) {
    return {
      ...base,
      standing: 'not-required',
      // The badge on this card reads "not required", and it means the tag: an
      // altered has a cage at every pace. The headline has to say which of the
      // two is being answered or the two read as contradicting each other.
      headline: 'No tag yet — NHRA’s own altered cage',
      why: 'Altereds running slower than 10.00 seconds meet NHRA’s own altered roll cage requirements, the illustration in general regulations 4:11, rather than an SFI specification.',
      lede:
        'At this pace an altered is on NHRA’s own drawing rather than an SFI number. It still has to be built to that drawing — the body style has never had the option of no cage — but you are not shopping for a spec sheet yet.',
      steps: [
        {
          what: 'Build to the illustration',
          detail: 'General regulations 4:11 carries the dimensions and the tubing sizes for an altered. That is the standard until 9.99.',
        },
        {
          what: 'When it changes',
          detail: '9.99, or 135 mph. Then the SFI altered specs apply and the car needs certifying and tagging.',
        },
      ],
      facts: [
        { label: 'Now', value: 'NHRA altered roll cage requirements.' },
        { label: 'At 9.99 or 135 mph', value: 'SFI 10.3 (7.50 and slower) or SFI 10.2A (6.00 to 7.49), certified and tagged.' },
      ],
      calm:
        'Being on the rulebook’s drawing rather than an SFI spec is not a lesser standard — it is the standard for the car you have, written by the same people.',
    };
  }
  const spec: ChassisSpec =
    ctx.et <= 7.49
      ? { spec: 'SFI 10.2A', title: 'Altered car roll cage — 6.00 to 7.49 seconds' }
      : { spec: 'SFI 10.3', title: 'Altered chassis — 7.50 seconds and slower' };
  return {
    ...base,
    headline: spec.spec,
    why: 'Open-bodied cars running 9.99 and quicker, or faster than 135 mph, must meet the applicable SFI specification for their ET.',
    lede:
      'Altereds have their own specs, separate from the full-bodied 25.x family, and this is the pace at which one of them starts to apply. From here it is the same errand every other certified car goes through: an inspector, a set of measurements, a serialised sticker.',
    steps: CHASSIS_STEPS,
    facts: [
      { label: spec.spec, value: spec.title },
      { label: 'Recertification', value: 'Every three years.' },
      { label: 'The tag names', value: 'A quickest permitted ET and a maximum permitted weight.' },
    ],
    calm:
      'The spec is a drawing with dimensions on it. Any shop that builds cages works to these, and the question worth asking before anything is cut is what the car will run when it is finished.',
  };
}
